import { useEffect, useState } from "react";
import { categoryService } from "../../services/category.service";
import { blogService } from "../../services/blog.service";
import { motion } from "framer-motion";
import {
  StaggerContainer,
  StaggerItem,
  fadeInScale,
  buttonHover,
  iconHover,
  navItemHover,
  itemVariants,
  containerVariants,
} from "../../animations";
import {
  MapPin,
  Phone,
  Mail,
  Send,
  Info,
  Shield,
  Award,
  Truck,
  AlertCircle,
  HeadphonesIcon,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  CheckCircle,
  Loader2,
} from "lucide-react";

const currentYear = new Date().getFullYear();

export default function Footer() {
  const [categories, setCategories] = useState([]);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    success: null,
    message: "",
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoryService.getActiveCategories();

        if (response.data && Array.isArray(response.data)) {
          const activeCategories = response.data
            .filter((category) => category.is_active)
            .slice(0, 8)
            .map((category) => ({
              id: category.id,
              name: category.name,
              icon: category.icon,
              slug:
                category.slug ||
                category.name.toLowerCase().replace(/\s+/g, "-"),
            }));

          setCategories(activeCategories);
        } else {
          // Fallback
          setCategories([
            { id: 1, name: "خوذات السلامة", slug: "safety-helmets" },
            { id: 2, name: "نظارات الحماية", slug: "safety-glasses" },
            { id: 3, name: "قفازات العمل", slug: "work-gloves" },
            { id: 4, name: "أحذية السلامة", slug: "safety-shoes" },
            { id: 5, name: "معدات الإطفاء", slug: "firefighting-equipment" },
            { id: 6, name: "صناديق الإسعافات", slug: "first-aid-kits" },
            {
              id: 7,
              name: "ملابس عالية الوضوح",
              slug: "high-visibility-clothing",
            },
            { id: 8, name: "معدات التنفس", slug: "respiratory-equipment" },
          ]);
        }
      } catch (error) {
        // Fallback
        setCategories([
          { id: 1, name: "خوذات السلامة", slug: "safety-helmets" },
          { id: 2, name: "نظارات الحماية", slug: "safety-glasses" },
          { id: 3, name: "قفازات العمل", slug: "work-gloves" },
          { id: 4, name: "أحذية السلامة", slug: "safety-shoes" },
          { id: 5, name: "معدات الإطفاء", slug: "firefighting-equipment" },
          { id: 6, name: "صناديق الإسعافات", slug: "first-aid-kits" },
          {
            id: 7,
            name: "ملابس عالية الوضوح",
            slug: "high-visibility-clothing",
          },
          { id: 8, name: "معدات التنفس", slug: "respiratory-equipment" },
        ]);
      }
    };

    fetchCategories();
  }, []);

  // Handle newsletter subscription
  const translateMessage = (message) => {
    if (!message) return "";

    const messageLower = message.toLowerCase();
    const translations = {
      // English to Arabic translations
      "please check your email to confirm your subscription":
        "يرجى التحقق من بريدك الإلكتروني لتأكيد الاشتراك",
      "email is already subscribed":
        "البريد الإلكتروني مسجل بالفعل في النشرة البريدية",
      "subscription reactivated successfully": "تم إعادة تنشيط الاشتراك بنجاح",
      "subscription created. please check your email to confirm.":
        "تم إنشاء الاشتراك. يرجى التحقق من بريدك الإلكتروني لتأكيد.",
      "invalid email address": "بريد إلكتروني غير صحيح",
      "email is required": "البريد الإلكتروني مطلوب",

      // Partial matches
      "check your email": "يرجى التحقق من بريدك الإلكتروني",
      "already subscribed": "مسجل بالفعل",
      reactivated: "تم إعادة التنشيط",
      "invalid email": "بريد إلكتروني غير صحيح",
      "email required": "البريد الإلكتروني مطلوب",
    };

    // Check for exact match first
    if (translations[messageLower]) {
      return translations[messageLower];
    }

    // Check for partial matches
    for (const [key, translation] of Object.entries(translations)) {
      if (messageLower.includes(key.toLowerCase())) {
        return translation;
      }
    }

    // Return original message if no translation found
    return message;
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    // Validate email
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setSubscriptionStatus({
        success: false,
        message: "يرجى إدخال بريد إلكتروني صحيح",
        type: "error",
      });
      return;
    }

    setIsSubmitting(true);
    setSubscriptionStatus({ success: null, message: "" });

    try {
      const response = await blogService.subscribeToNewsletter({
        email: email,
        name: null,
        preferences: null,
        source: "footer_newsletter",
      });

      // Extract the actual response data
      const result = response.data || response;

      // Check different success indicators
      const isSuccess = response.success || result.success || result.subscribed;

      // Check if already subscribed (but pending confirmation)
      const isAlreadySubscribed = result.already_subscribed === true;

      // Get subscription_id if available
      const subscriptionId = result.subscription_id;

      // Get the message from the right place
      const apiMessage = result.message || response.message || "";

      // Translate the message
      const translatedMessage = translateMessage(apiMessage);

      if (isSuccess) {
        // Handle different subscription scenarios
        if (isAlreadySubscribed) {
          // Already subscribed but pending confirmation
          setSubscriptionStatus({
            success: true,
            message:
              translatedMessage ||
              "البريد الإلكتروني مسجل مسبقاً. يرجى تفقد بريدك الإلكتروني لتأكيد الاشتراك",
            type: "info",
            subscriptionId: subscriptionId,
          });

          // ✅ Reset email input for already subscribed case
          setEmail("");

          // Also reset the actual form input if you have direct DOM access
          if (e.target) {
            e.target.reset(); // This resets all form fields
          }
        } else {
          // New subscription
          setSubscriptionStatus({
            success: true,
            message:
              translatedMessage ||
              "تم الاشتراك بنجاح! يرجى تفقد بريدك الإلكتروني لتأكيد الاشتراك",
            type: "success",
            subscriptionId: subscriptionId,
          });

          // ✅ Reset email input for new subscription
          setEmail("");

          // Reset the form
          if (e.target) {
            e.target.reset();
          }
        }

        // Clear status after timeout
        const timeoutDuration = isAlreadySubscribed ? 8000 : 5000;
        setTimeout(() => {
          setSubscriptionStatus({ success: null, message: "" });
        }, timeoutDuration);
      } else {
        // Handle error cases
        let errorMessage =
          translatedMessage || "فشل الاشتراك، يرجى المحاولة مرة أخرى";

        // Check for specific error types
        if (
          apiMessage.toLowerCase().includes("already subscribed") ||
          apiMessage.toLowerCase().includes("already exists")
        ) {
          errorMessage =
            "هذا البريد الإلكتروني مشترك بالفعل في النشرة البريدية";
        } else if (apiMessage.toLowerCase().includes("invalid email")) {
          errorMessage = "البريد الإلكتروني غير صحيح";
        } else if (apiMessage.toLowerCase().includes("bounced")) {
          errorMessage =
            "لا يمكن الاشتراك بهذا البريد الإلكتروني (تم رفضه سابقاً)";
        }

        setSubscriptionStatus({
          success: false,
          message: errorMessage,
          type: "error",
        });
      }
    } catch (error) {
      let errorMessage = "حدث خطأ أثناء الاشتراك، يرجى المحاولة مرة أخرى";

      // Handle different error types
      if (error.response?.data?.message) {
        errorMessage = translateMessage(error.response.data.message);
      } else if (error.message) {
        errorMessage = translateMessage(error.message);
      } else if (!navigator.onLine) {
        errorMessage = "لا يوجد اتصال بالإنترنت. يرجى التحقق من اتصالك";
      }

      setSubscriptionStatus({
        success: false,
        message: errorMessage,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: Shield,
      title: "جودة مضمونة",
      description: "منتجاتنا تخضع لأعلى معايير الجودة والسلامة",
    },
    {
      icon: Award,
      title: "شهادات معتمدة",
      description: "جميع المنتجات معتمدة من هيئات السلامة العالمية",
    },
    {
      icon: Truck,
      title: "شحن سريع",
      description: "توصيل سريع لجميع أنحاء مصر خلال 2-3 أيام",
    },
    {
      icon: HeadphonesIcon,
      title: "دعم فني",
      description: "فريق دعم فني متاح على مدار الساعة",
    },
  ];

  const quickLinks = [
    { label: "الرئيسية", href: "/" },
    { label: "المدونة", href: "/blog" },
    { label: "العروض", href: "/sales" },
    { label: "طلب بيع", href: "/request" },
    { label: "اتصل بنا", href: "/contact" },
  ];

  const socialLinks = [
    {
      icon: Facebook,
      href: "#",
      label: "فيسبوك",
    },
    {
      icon: Twitter,
      href: "#",
      label: "تويتر",
    },
    {
      icon: Instagram,
      href: "#",
      label: "انستجرام",
    },
    {
      icon: Linkedin,
      href: "#",
      label: "لينكد إن",
    },
    {
      icon: Youtube,
      href: "#",
      label: "يوتيوب",
    },
  ];

  const legalLinks = [
    { label: "سياسة الخصوصية", href: "/privacy" },
    { label: "شروط الخدمة", href: "/terms" },
    { label: "سياسة الإرجاع", href: "/returns" },
    { label: "الأسئلة الشائعة", href: "/faqs" },
  ];

  return (
    <motion.footer
      className="relative overflow-hidden transition-colors duration-300"
      style={{
        background: "var(--bg-primary)",
      }}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      variants={containerVariants}
    >
      {/* Background Gradient */}
      <div
        className="absolute inset-0 transition-colors duration-300"
        style={{
          background: "var(--gradient-subtle)",
        }}
      />

      {/* Features Strip */}
      <motion.div
        className="relative transition-colors duration-300"
        style={{
          borderBottom: "var(--glass-border)",
        }}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="container mx-auto px-4 py-8">
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <StaggerItem key={index}>
                <motion.div
                  className="flex items-center space-x-4 space-x-reverse p-4 rounded-2xl group cursor-pointer transition-colors duration-300"
                  whileHover="hover"
                  whileTap="tap"
                  variants={navItemHover}
                  style={{
                    background: "var(--bg-glass)",
                    backdropFilter: "var(--backdrop-blur)",
                    border: "var(--glass-border)",
                  }}
                >
                  <motion.div
                    className="flex items-center justify-center w-12 h-12 rounded-xl transition-colors duration-300"
                    style={{
                      background: "var(--gradient-primary)",
                    }}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <feature.icon className="w-6 h-6 text-white" />
                  </motion.div>
                  <div className="flex-1">
                    <h4
                      className="font-bold transition-colors duration-300 group-hover:text-primary-500"
                      style={{
                        color: "var(--text-primary)",
                      }}
                    >
                      {feature.title}
                    </h4>
                    <p
                      className="text-sm mt-1 transition-colors duration-300"
                      style={{
                        color: "var(--text-tertiary)",
                      }}
                    >
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </motion.div>

      {/* Main Footer Content */}
      <div className="relative container mx-auto px-4 py-16 md:py-20">
        <StaggerContainer className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 gap-10 lg:gap-14">
          {/* Brand & Description */}
          <StaggerItem className="xl:col-span-2">
            <motion.div variants={fadeInScale} className="space-y-6">
              {/* Logo */}
              <motion.div
                className="flex items-center space-x-3 space-x-reverse transition-colors duration-300"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <motion.a href="/">
                  <div className="relative w-12 h-12 rounded-2xl overflow-hidden">
                    <motion.img
                      src="/logo.png"
                      alt="بروتاكـتا"
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                    />
                    <motion.div
                      className="absolute inset-0 transition-colors duration-300"
                      style={{
                        background: "var(--bg-glass)",
                      }}
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                  <div>
                    <h3
                      className="text-3xl font-bold transition-colors duration-300"
                      style={{
                        background: "var(--gradient-primary)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        color: "transparent",
                      }}
                    >
                      بروتاكـتا
                    </h3>
                    <p
                      className="text-sm mt-1 transition-colors duration-300"
                      style={{
                        color: "var(--text-tertiary)",
                      }}
                    >
                      السلامة أولاً
                    </p>
                  </div>
                </motion.a>
              </motion.div>

              {/* Description */}
              <motion.p
                className="leading-relaxed text-lg max-w-md transition-colors duration-300"
                style={{
                  color: "var(--text-secondary)",
                }}
                variants={itemVariants}
              >
                شريكك الموثوق في توفير معدات السلامة المهنية عالية الجودة. نلتزم
                بحماية العاملين في مختلف المجالات بأفضل الحلول الأمنية.
              </motion.p>

              {/* Social Links */}
              <motion.div
                className="flex space-x-3 space-x-reverse"
                variants={itemVariants}
              >
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    aria-label={social.label}
                    className="group p-3 rounded-xl transition-all duration-300"
                    variants={iconHover}
                    whileHover="hover"
                    whileTap="tap"
                    style={{
                      background: "var(--bg-glass)",
                      backdropFilter: "var(--backdrop-blur)",
                      border: "var(--glass-border)",
                    }}
                  >
                    <social.icon
                      className="w-5 h-5 transition-colors duration-300 group-hover:text-primary-500"
                      style={{
                        color: "var(--text-secondary)",
                      }}
                    />
                  </motion.a>
                ))}
              </motion.div>
            </motion.div>
          </StaggerItem>

          {/* Quick Links */}
          <StaggerItem>
            <motion.div variants={fadeInScale} className="space-y-6">
              <h4
                className="text-xl font-bold flex items-center space-x-3 space-x-reverse transition-colors duration-300"
                style={{
                  color: "var(--text-primary)",
                }}
              >
                <div
                  className="w-1 h-8 rounded-full transition-colors duration-300"
                  style={{
                    background: "var(--gradient-primary)",
                  }}
                />
                <span>روابط سريعة</span>
              </h4>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <motion.li key={index} variants={itemVariants}>
                    <motion.a
                      href={link.href}
                      className="flex items-center space-x-3 space-x-reverse transition-all duration-300 group py-2"
                      variants={navItemHover}
                      whileHover={{ x: -4 }}
                      style={{
                        color: "var(--text-secondary)",
                      }}
                    >
                      <span className="font-medium group-hover:text-primary-500 transition-colors duration-300">
                        {link.label}
                      </span>
                      <motion.div
                        className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{
                          color: "var(--primary-500)",
                        }}
                        whileHover={{ x: -4 }}
                      >
                        <svg fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14 18l-6-6 6-6 1.41 1.41L10.83 12l4.58 4.59z" />
                        </svg>
                      </motion.div>
                    </motion.a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </StaggerItem>

          {/* Categories */}
          <StaggerItem>
            <motion.div variants={fadeInScale} className="space-y-6">
              <h4
                className="text-xl font-bold flex items-center space-x-3 space-x-reverse transition-colors duration-300"
                style={{
                  color: "var(--text-primary)",
                }}
              >
                <div
                  className="w-1 h-8 rounded-full transition-colors duration-300"
                  style={{
                    background: "var(--gradient-primary)",
                  }}
                />
                <span>فئات المنتجات</span>
              </h4>

              <ul className="space-y-3">
                {categories.map((category, index) => {
                  return (
                    <motion.li key={category.id} variants={itemVariants}>
                      <motion.a
                        href={`/categories/${category.slug || category.id}`}
                        className="flex items-center space-x-3 space-x-reverse transition-all duration-300 group py-2"
                        whileHover={{ x: -8 }}
                        style={{
                          color: "var(--text-secondary)",
                        }}
                      >
                        <motion.div
                          className="w-5 h-5 transition-colors duration-300 flex items-center justify-center"
                          style={{
                            color: "var(--accent-gold)",
                          }}
                          whileHover={{ scale: 1.2, rotate: 15 }}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </motion.div>
                        <span className="font-medium group-hover:text-accent-gold transition-colors duration-300">
                          {category.name}
                        </span>
                      </motion.a>
                    </motion.li>
                  );
                })}
              </ul>
            </motion.div>
          </StaggerItem>

          {/* Contact & Newsletter */}
          <StaggerItem>
            <motion.div variants={fadeInScale} className="space-y-8">
              <h4
                className="text-xl font-bold flex items-center space-x-3 space-x-reverse transition-colors duration-300"
                style={{
                  color: "var(--text-primary)",
                }}
              >
                <div
                  className="w-1 h-8 rounded-full transition-colors duration-300"
                  style={{
                    background: "var(--gradient-primary)",
                  }}
                />
                <span>تواصل معنا</span>
              </h4>

              <div className="space-y-4">
                {/* Address */}
                <motion.div
                  className="flex space-x-4 space-x-reverse group transition-colors duration-300"
                  variants={itemVariants}
                >
                  <div
                    className="flex items-center justify-center w-12 h-12 rounded-xl transition-colors duration-300"
                    style={{
                      background: "var(--bg-glass)",
                      backdropFilter: "var(--backdrop-blur)",
                    }}
                  >
                    <MapPin
                      className="w-5 h-5 transition-colors duration-300"
                      style={{
                        color: "var(--primary-500)",
                      }}
                    />
                  </div>
                  <div>
                    <p
                      className="font-medium transition-colors duration-300"
                      style={{
                        color: "var(--text-primary)",
                      }}
                    >
                      العنوان
                    </p>
                    <p
                      className="text-sm mt-1 transition-colors duration-300"
                      style={{
                        color: "var(--text-tertiary)",
                      }}
                    >
                      كفر الدوار
                      <br />
                      محافظة البحيرة، مصر
                    </p>
                  </div>
                </motion.div>

                {/* Phone */}
                <motion.div
                  className="flex space-x-4 space-x-reverse group transition-colors duration-300"
                  variants={itemVariants}
                >
                  <div
                    className="flex items-center justify-center w-12 h-12 rounded-xl transition-colors duration-300"
                    style={{
                      background: "var(--bg-glass)",
                      backdropFilter: "var(--backdrop-blur)",
                    }}
                  >
                    <Phone
                      className="w-5 h-5 transition-colors duration-300"
                      style={{
                        color: "var(--primary-500)",
                        transform: "rotate(260deg)",
                      }}
                    />
                  </div>
                  <div>
                    <p
                      className="font-medium transition-colors duration-300"
                      style={{
                        color: "var(--text-primary)",
                      }}
                    >
                      الهاتف
                    </p>
                    <p
                      className="text-sm mt-1 transition-colors duration-300"
                      style={{
                        color: "var(--text-tertiary)",
                      }}
                      dir="ltr"
                    >
                      +20 106 091 7992
                    </p>
                  </div>
                </motion.div>

                {/* Email */}
                <motion.div
                  className="flex space-x-4 space-x-reverse group transition-colors duration-300"
                  variants={itemVariants}
                >
                  <div
                    className="flex items-center justify-center w-12 h-12 rounded-xl transition-colors duration-300"
                    style={{
                      background: "var(--bg-glass)",
                      backdropFilter: "var(--backdrop-blur)",
                    }}
                  >
                    <Mail
                      className="w-5 h-5 transition-colors duration-300"
                      style={{
                        color: "var(--primary-500)",
                      }}
                    />
                  </div>
                  <div>
                    <p
                      className="font-medium transition-colors duration-300"
                      style={{
                        color: "var(--text-primary)",
                      }}
                    >
                      البريد الإلكتروني
                    </p>
                    <p
                      className="text-sm mt-1 transition-colors duration-300"
                      style={{
                        color: "var(--text-tertiary)",
                      }}
                    >
                      support@protacta.com
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Newsletter */}
              <motion.div
                className="pt-6 transition-colors duration-300"
                style={{
                  borderTop: "var(--glass-border)",
                }}
                variants={itemVariants}
              >
                <h5
                  className="font-bold mb-4 transition-colors duration-300"
                  style={{
                    color: "var(--text-primary)",
                  }}
                >
                  النشرة البريدية
                </h5>
                <p
                  className="text-sm mb-4 transition-colors duration-300"
                  style={{
                    color: "var(--text-tertiary)",
                  }}
                >
                  اشترك لتصلك آخر العروض والمنتجات الجديدة
                </p>

                {/* Status Message */}
                {subscriptionStatus.message && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mb-4 p-3 rounded-xl text-sm font-medium flex items-center space-x-2 space-x-reverse ${
                      subscriptionStatus.success === true
                        ? subscriptionStatus.type === "info"
                          ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800"
                          : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800"
                        : subscriptionStatus.success === false
                          ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800"
                          : "bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-300 border-gray-200 dark:border-gray-700"
                    }`}
                    style={{
                      border: "var(--glass-border)",
                    }}
                  >
                    {/* Icon based on status type */}
                    <div className="flex-shrink-0">
                      {subscriptionStatus.success === true ? (
                        subscriptionStatus.type === "info" ? (
                          <Info className="w-5 h-5" />
                        ) : (
                          <CheckCircle className="w-5 h-5" />
                        )
                      ) : subscriptionStatus.success === false ? (
                        <AlertCircle className="w-5 h-5" />
                      ) : (
                        <Info className="w-5 h-5" />
                      )}
                    </div>

                    {/* Message text */}
                    <span className="flex-1">{subscriptionStatus.message}</span>

                    {/* Optional close button */}
                    {subscriptionStatus.dismissible && (
                      <button
                        onClick={() =>
                          setSubscriptionStatus({ success: null, message: "" })
                        }
                        className="flex-shrink-0 ml-2 hover:opacity-75 transition-opacity"
                        aria-label="Close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </motion.div>
                )}

                <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                  <motion.input
                    type="email"
                    placeholder="بريدك الإلكتروني"
                    className="w-full px-4 py-3 rounded-xl transition-all duration-300"
                    whileFocus={{ scale: 1.02 }}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    required
                    style={{
                      background: "var(--bg-glass)",
                      backdropFilter: "var(--backdrop-blur)",
                      border: "var(--glass-border)",
                      color: "var(--text-primary)",
                    }}
                  />
                  <motion.button
                    type="submit"
                    className="w-full flex items-center justify-center space-x-2 space-x-reverse py-3 px-6 rounded-xl font-bold text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    variants={buttonHover}
                    whileHover="hover"
                    whileTap="tap"
                    disabled={isSubmitting}
                    style={{
                      background: "var(--gradient-primary)",
                      boxShadow: "var(--shadow-glow)",
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>جاري الاشتراك...</span>
                      </>
                    ) : (
                      <>
                        <span>اشتراك</span>
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </motion.button>
                </form>
              </motion.div>
            </motion.div>
          </StaggerItem>
        </StaggerContainer>
      </div>

      {/* Bottom Bar */}
      <motion.div
        className="relative transition-colors duration-300"
        style={{
          background: "var(--bg-primary)",
          borderTop: "var(--glass-border)",
        }}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
            {/* Left side - Copyright */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <p
                className="text-center lg:text-right transition-colors duration-300"
                style={{
                  color: "var(--text-primary)",
                }}
              >
                © {currentYear}{" "}
                <span
                  className="font-bold transition-colors duration-300"
                  style={{
                    color: "var(--primary-600)",
                  }}
                >
                  بروتاكـتا
                </span>
                . جميع الحقوق محفوظة.
              </p>
            </motion.div>

            {/* Right side - Legal links */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="flex flex-wrap justify-center gap-6">
                {legalLinks.map((link, index) => (
                  <motion.a
                    key={index}
                    href={link.href}
                    className="text-sm font-medium transition-colors duration-300 hover:text-primary-500"
                    style={{
                      color: "var(--text-secondary)",
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {link.label}
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </motion.footer>
  );
}
